import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, share, tap } from 'rxjs/operators';

import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { AdminFeatureFlagService } from '@services/feature-flags/admin';
import { FleetViewFeatureFlagService } from './feature-flags/fleet-view';
import { DriverAppFeatureFlagService } from '@services/feature-flags/driver-app';
import { EnterprisePlatformFeatureFlagService } from '@services/feature-flags/enterprise-platform';
import { FacilityMonitoringFeatureFlagService } from '@services/feature-flags/facility-monitoring';
import { FleetGrowthFeatureFlagService } from '@services/feature-flags/growth';
import { MotiveGptFeatureFlagService } from '@services/feature-flags/motive-gpt';
import { GroupLevelSettingsConfigService } from './dynamic-configs/group-level-settings-config';
import { ComplianceFeatureFlagService } from './feature-flags/compliance';
import { CoachingFeatureFlagService } from './feature-flags/coaching';
import { EntCustomReportingDynamicConfigService } from './dynamic-configs/ent-custom-reporting-enabled';
import { Session } from '@services/session';
import { LocalStorage } from '@app/services/local-storage';

import { ACCOUNT_NAV_ITEM, INavItem, IStatsigFlag } from '@app/layout/sidebar-2.0/sidebar.constants';

import { setCookie, getCookie } from '../lib/cookies';
import { CookieKey } from '@lib/cookie-keys.const';
import {
  ADMIN_NAV_ITEMS as NEW_ADMIN_NAV_ITEMS,
  DASHBOARD_NAV_ITEMS as NEW_DASHBOARD_NAV_ITEMS,
} from '@app/layout/sidebar-2.0/sidebar.constants';

import { getMessagingBootPromise } from '@app/dashboard/messaging/lib/boot';
import { SmartLoadBoardFeatureFlagService } from './feature-flags/smart-load-board';
import { WorkforceHubFeatureFlagService } from '@services/feature-flags/workforce-hub';
import { DeviceHubFeatureFlagService } from './feature-flags/device-hub';
import { FleetCardsFeatureFlagService } from './feature-flags/fleet-cards';
import { FuelFeatureFlagService } from './feature-flags/fuel';

export type IPrimaryLink = 'Dashboard' | 'Admin' | 'Account';
export type ISidebarState = 'expanded' | 'collapsed';

export interface INavigation {
  navItems: INavItem[];
  hasGroups: boolean;
  backLink: INavItem;
  sidebarPrimaryLink: IPrimaryLink;
}

interface ICounters {
  message: number;
  drivers: number;
  fleetManagers: number;
  admin: number;
  marketplace: number;
  fleetUsers: number;
  coaching: number;
}

const sidebarStatusCookieKey: CookieKey = CookieKey.SIDE_BAR_STATUS;

@Injectable({ providedIn: 'root' })
export class SidebarService {
  public triggerChangeDetection$: Subject<void> = new Subject<void>(); // used to trigger change detection in sidebar
  public toggleChatBot: Subject<void> = new Subject<void>();
  public readonly sidebarVisibility$: Subject<boolean> = new Subject<boolean>();
  private currentSidebar: BehaviorSubject<IPrimaryLink>;
  private currentSidebar$: Observable<IPrimaryLink>;
  private sidebarState: BehaviorSubject<ISidebarState>;
  private sidebarState$: Observable<ISidebarState>;
  private expandableSidebar: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private navCounterSubject: BehaviorSubject<ICounters>;
  private navCounters$: Observable<ICounters>;
  private navListener$: Observable<INavigation>;
  private myCardModuleLoaded$: Subject<boolean> = new Subject<boolean>();
  private showMyCardDrawer$: Subject<boolean> = new Subject<boolean>();
  private currentUrl: string = '';
  private previousUrl: string = '';
  private readonly excludeBackUrl = '/settings';
  private messagingUnreadCountService;

  constructor(
    private injector: Injector,
    private session: Session,
    private driverAppFeatureFlagService: DriverAppFeatureFlagService,
    private facilityMonitoringFeatureFlagService: FacilityMonitoringFeatureFlagService,
    private fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
    private fgFeatureFlagService: FleetGrowthFeatureFlagService,
    private fvFeatureFlagService: FleetViewFeatureFlagService,
    private epFeatureFlagService: EnterprisePlatformFeatureFlagService,
    private adminFeatureFlagService: AdminFeatureFlagService,
    private motiveGptFeatureFlagService: MotiveGptFeatureFlagService,
    private complianceFeatureFlagService: ComplianceFeatureFlagService,
    private fuelFeatureFlagService: FuelFeatureFlagService,
    private smartLoadBoardFeatureFlagService: SmartLoadBoardFeatureFlagService,
    private coachingFeatureFlagService: CoachingFeatureFlagService,
    private workforceHubFeatureFlagService: WorkforceHubFeatureFlagService,
    private glsConfig: GroupLevelSettingsConfigService,
    private deviceHubFeatureFlagService: DeviceHubFeatureFlagService,
    private customReportingDynamicConfigService: EntCustomReportingDynamicConfigService,
    private router: Router,
    private localStorage: LocalStorage,
  ) {
    this.initializeEmitters();
    this.initializeListener();
    this.updateRecommendedAppCount();
    this.subscribeToRecommendedAppsCount();
    if (this.session.isChatAllowed()) this.loadChannelsService();

    if (
      this.session.userPermissions.isFleetUsersManageAllowed() ||
      this.session.userPermissions.isDriversManageAllowed()
    ) {
      this.refreshPendingCounters();
    }
  }

  private readonly DASHBOARD_FEATURE_FLAG_SERVICES: Readonly<IStatsigFlag> = {
    fleetcardsFeatureFlagService: this.fleetCardsFeatureFlagService,
    facilityMonitoringFeatureFlagService: this.facilityMonitoringFeatureFlagService,
    fgFeatureFlagService: this.fgFeatureFlagService,
    fvFeatureFlagService: this.fvFeatureFlagService,
    enterprisePlatformFeatureFlagService: this.epFeatureFlagService,
    motiveGptFeatureFlagService: this.motiveGptFeatureFlagService,
    complianceFeatureFlagService: this.complianceFeatureFlagService,
    smartLoadBoardFeatureFlagService: this.smartLoadBoardFeatureFlagService,
    coachingFeatureFlagService: this.coachingFeatureFlagService,
    workforceHubFeatureFlagService: this.workforceHubFeatureFlagService,
    deviceHubFeatureFlagService: this.deviceHubFeatureFlagService,
    customReportingDynamicConfigService: this.customReportingDynamicConfigService,
    fuelFeatureFlagService: this.fuelFeatureFlagService,
  };

  private readonly ADMIN_FEATURE_FLAG_SERVICES: Readonly<IStatsigFlag> = {
    fleetcardsFeatureFlagService: this.fleetCardsFeatureFlagService,
    driverAppFeatureFlagService: this.driverAppFeatureFlagService,
    fgFeatureFlagService: this.fgFeatureFlagService,
    enterprisePlatformFeatureFlagService: this.epFeatureFlagService,
    adminFeatureFlagService: this.adminFeatureFlagService,
    complianceFeatureFlagService: this.complianceFeatureFlagService,
    smartLoadBoardFeatureFlagService: this.smartLoadBoardFeatureFlagService,
    coachingFeatureFlagService: this.coachingFeatureFlagService,
    workforceHubFeatureFlagService: this.workforceHubFeatureFlagService,
    fuelFeatureFlagService: this.fuelFeatureFlagService,
    glsConfig: this.glsConfig,
  };

  public get navListener(): Observable<INavigation> {
    return this.navListener$;
  }

  public get stateListener(): Observable<ISidebarState> {
    return this.sidebarState$;
  }

  public setPrimaryLink(nav: IPrimaryLink): void {
    this.currentSidebar.next(nav);
  }

  public setState(state: ISidebarState): void {
    this.sidebarState.next(state);
  }

  public setCurrentUrl(url: string): void {
    if (!this.currentUrl.startsWith(this.excludeBackUrl)) this.previousUrl = this.currentUrl;
    this.currentUrl = url;
  }

  public refreshPendingCounters(): void {
    this.session.getPendingCounts()
      .subscribe(counters => {
        this.navCounterSubject.next({
          ...this.navCounterSubject.value,
          fleetManagers: counters.pending_fleet_users,
          drivers: this.session.userPermissions.isDriversManageAllowed() ? counters.pending_drivers : 0,
          admin: counters.pending_fleet_users + counters.pending_drivers,
          fleetUsers: counters.pending_fleet_users,
          coaching: counters.pending_coaching,
        });
      });
  }

  public setSidebarVisibility(isVisible: boolean): void {
    this.sidebarVisibility$.next(isVisible);
  }

  public setExpandableSidebar(isExpandable: boolean): void {
    this.expandableSidebar.next(isExpandable);
  }

  public getExpandableSidebar(): Observable<boolean> {
    return this.expandableSidebar.asObservable();
  }

  private initializeEmitters(): void {
    this.currentSidebar = new BehaviorSubject(null);
    this.currentSidebar$ = this.currentSidebar.pipe(
      distinctUntilChanged(),
      filter(val => Boolean(val)),
    );

    this.sidebarState = new BehaviorSubject(this.getSidebarStatusFromCookie());
    this.sidebarState$ = this.sidebarState.pipe(
      tap(state => setCookie(sidebarStatusCookieKey, state)),
    );

    this.navCounterSubject = new BehaviorSubject({
      message: null,
      fleetManagers: null,
      drivers: null,
      admin: null,
      marketplace: null,
      fleetUsers: null,
      coaching: null,
    });
    this.navCounters$ = this.navCounterSubject.pipe(
      filter(val => Boolean(val)),
    );
  }

  private getSidebarStatusFromCookie(): ISidebarState {
    return getCookie(sidebarStatusCookieKey) || 'expanded';
  }

  private initializeListener(): void {
    this.navListener$ = combineLatest(
      this.currentSidebar$,
      this.navCounters$,
      (nav: IPrimaryLink, counters: ICounters): INavigation => {
        let navigation: INavigation;
        if (nav === 'Admin') navigation = this.getAdminSidebar();
        else if (nav === 'Account') navigation = this.getDashboardSidebar();
        else navigation = this.getDashboardSidebar();
        navigation.navItems.forEach((item: INavItem) => {
          item.count = counters[item.name] || 0;
        });
        return navigation;
      },
    );
  }

  private getDashboardSidebar(): INavigation {
    const dashboardNavItems = NEW_DASHBOARD_NAV_ITEMS;
    return {
      navItems: dashboardNavItems.filter(nav => nav.shouldNavItemShow(this.session, this.DASHBOARD_FEATURE_FLAG_SERVICES)),
      hasGroups: true,
      sidebarPrimaryLink: 'Dashboard',
      backLink: {
        label: $localize`Admin`,
        link: '/admin/company',
        name: 'admin',
        shouldNavItemShow: () => this.session.userPermissions.hasAdministrativePermission(),
        count: this.navCounterSubject.value.admin,
      },
    };
  }

  public getAdminSidebar(): INavigation {
    const adminNavItems = NEW_ADMIN_NAV_ITEMS;
    return {
      navItems: adminNavItems.filter(nav => nav.shouldNavItemShow(this.session, this.ADMIN_FEATURE_FLAG_SERVICES)),
      hasGroups: false,
      sidebarPrimaryLink: 'Admin',
      backLink: {
        label: $localize`Dashboard`,
        link: '/',
        name: 'dashboard',
        shouldNavItemShow: () => true,
      },
    };
  }

  public isRouteEnabled(routeLink: string): boolean {
    const isAdminRoute: boolean = routeLink.includes('/admin');
    const navItems: INavItem[] = isAdminRoute ? NEW_ADMIN_NAV_ITEMS : NEW_DASHBOARD_NAV_ITEMS;
    const navItem: INavItem = navItems.find(nav => nav.link === routeLink);
    return navItem.shouldNavItemShow(
      this.session,
      isAdminRoute ? this.ADMIN_FEATURE_FLAG_SERVICES : this.DASHBOARD_FEATURE_FLAG_SERVICES,
    );
  }

  public getAccountSidebar(): INavigation {
    if (!this.currentUrl.startsWith(this.excludeBackUrl)) this.previousUrl = this.currentUrl;
    const urlWithParams = this.previousUrl ? this.splitUrlStringToUrlAndParams(this.previousUrl) : [['/'], {}];
    return {
      navItems: ACCOUNT_NAV_ITEM.filter(nav => nav.shouldNavItemShow(this.session)),
      hasGroups: false,
      sidebarPrimaryLink: 'Account',
      backLink: {
        label: $localize`Go Back`,
        link: urlWithParams[0],
        params: urlWithParams[1],
        name: 'back',
        shouldNavItemShow: () => true,
      },
    };
  }

  private splitUrlStringToUrlAndParams(url: string): [(any[] | string), {[key: string]: string}] {
    const parsedUrl = this.router.parseUrl(url);
    const segments = parsedUrl.root.children.primary.segments;

    const path = segments.map(it => it.path).join('/');
    const params = segments[segments.length - 1].parameters;

    // for query params that appear after ?
    const queryParams = parsedUrl.queryParams;

    return [
      [path, params],
      queryParams,
    ];
  }

  private async loadChannelsService(): Promise<void> {
    await getMessagingBootPromise();

    // tslint:disable-next-line: no-shadowed-variable
    const { MessagingUnreadCountService } = await import('@app/dashboard/messaging/services/messaging-unread-count.service');
    this.messagingUnreadCountService = this.injector.get(MessagingUnreadCountService);
    this.subscribeToUnreadChannelCount();
  }

  private subscribeToUnreadChannelCount(): void {
    this.messagingUnreadCountService.unreadChannelCountUpdate$
      .pipe(share())
      .subscribe(message => this.navCounterSubject.next({
        ...this.navCounterSubject.value,
        message,
      }));
  }

  private subscribeToRecommendedAppsCount(): void {
    this.session.sessionUpdate$
      .subscribe(this.updateRecommendedAppCount.bind(this));
  }

  private updateRecommendedAppCount(): void {
    const recommendedAppNotificationHidden: boolean = this.session.isRecommendedAppNotificationHidden()
      || !this.session.isAdmin();
    this.getDashboardSidebar().navItems.forEach((item: INavItem) => {
      if (item.name === 'marketplace') {
        this.setNotificationDot(item, !recommendedAppNotificationHidden);
      }
    });
    this.setFleetCardNotificationDot();
  }

  private setNotificationDot(item: INavItem, showNotificationDot: boolean): void {
    item.showNotificationDot = showNotificationDot;
    this.navCounterSubject.next({
      ...this.navCounterSubject.value,
    });
  }

  public setFleetCardNotificationDot(overrideLocalStorage: boolean = false, value: boolean = false): void {
    let showNotificationDot: boolean;
    if (overrideLocalStorage) {
      showNotificationDot = value;
    } else {
      showNotificationDot = this.localStorage.getFleetCardNotificationDot();
    }
    const dashboardItem: INavItem = this.getDashboardSidebar().navItems
      .find((navItem: INavItem) => navItem.name === 'fleetCards');
    const adminItem: INavItem = this.getAdminSidebar().navItems
      .find((navItem: INavItem) => navItem.name === 'fleetCards');
    if (dashboardItem) dashboardItem.showNotificationDot = showNotificationDot;
    if (adminItem) adminItem.showNotificationDot = showNotificationDot;
    this.triggerChangeDetection$.next();
  }

  public setMyCardsModuleLoaded(): void {
    this.myCardModuleLoaded$.next(true);
  }

  public isMyCardsModuleLoaded(): Observable<boolean> {
    return this.myCardModuleLoaded$.asObservable();
  }

  public showMyCardDrawer(): void {
    this.showMyCardDrawer$.next(true);
  }

  public getShowMyCardDrawerListener(): Observable<boolean> {
    return this.showMyCardDrawer$;
  }
}
